import { Link, Navigate, useParams } from "react-router-dom";
import { CheckCircle2, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { getService, SERVICES } from "@/data/services";
import { whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <div data-testid={`service-detail-${slug}`}>
      <Seo
        title={`${service.name} | MBA Project & Thesis Writing Help`}
        description={service.short}
      />
      <PageHero eyebrow="Service" lines={service.name.split(" & ").length > 1 ? service.name.split(" & ").map((p, i) => (i === 0 ? `${p} &` : p)) : [service.name]} sub={service.intro} />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>What's included</Eyebrow>
            <ul className="space-y-4">
              {service.included.map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-600 leading-relaxed">
                  <CheckCircle2 className="h-5 w-5 text-[#2563EB] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-sm md:text-base">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="mt-16">
            <Eyebrow>Our process</Eyebrow>
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              {service.process.map((step, i) => (
                <div key={step} className="flex items-center gap-6 py-6">
                  <span className="font-display font-extrabold text-3xl md:text-4xl text-slate-200 w-14 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm md:text-base text-slate-600 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {service.why && (
            <Reveal className="mt-16">
              <Eyebrow>Why students choose us for this</Eyebrow>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed" data-testid="service-why-text">{service.why}</p>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-5">
          <Reveal className="lg:sticky lg:top-24 border border-slate-200 rounded-2xl p-8 bg-[#F5F8FF]">
            <div className="overflow-hidden rounded-md border border-slate-200 mb-6">
              <img src={service.image} alt={service.name} className="w-full h-44 object-cover" />
            </div>
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-[#2563EB] mt-0.5" strokeWidth={1.5} />
              <div>
                <h3 className="font-display font-bold text-base tracking-tight">Turnaround time</h3>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{service.turnaround}</p>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-200">
              <h3 className="font-display font-bold text-xl tracking-tight">Get a fixed quote today</h3>
              <p className="mt-2 text-sm text-slate-600">Send your guidelines and deadline — we'll respond with a scoped plan.</p>
              <a
                href={whatsappWithText(`Hi, I need help with ${service.name}. Can you share a quote?`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-detail-whatsapp-cta"
                className="mt-6 inline-flex w-full justify-center items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="h-5 w-5" /> Discuss on WhatsApp
              </a>
              <Link
                to="/pricing"
                className="mt-3 inline-flex w-full justify-center items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-semibold text-[#1E2A78] transition-colors duration-200 hover:bg-white"
              >
                See Pricing
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-[#F5F8FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex items-center justify-between">
          <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#1E2A78] transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> All services
          </Link>
          <Link to={`/services/${next.slug}`} data-testid="next-service-link" className="inline-flex items-center gap-2 text-sm font-medium text-[#1E2A78] hover:text-[#2563EB] transition-colors duration-200">
            Next: {next.name} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
